"use client";

/**
 * WhiteboardCard – renders one whiteboard exercise spec.
 *
 * The shell follows the dark session design language (DESIGN.md): slate panel,
 * orange accent, 16px radius. Intentionally compact — the whiteboard is a prop
 * the tutor holds up for a short moment, not a separate app.
 */
import {
  EMPTY_WHITEBOARD_RENDER_STATE,
  type WhiteboardConnection,
  type WhiteboardDrawing,
  type WhiteboardRenderState,
} from "@/lib/whiteboard/boardState";
import type { WhiteboardSpec } from "@/lib/whiteboard/spec";
import { ArrowRight, LocateFixed, MessageSquareText, PenLine } from "lucide-react";
import React, { useLayoutEffect, useRef, useState } from "react";
import { ComposedCard } from "./ComposedCard";
import { EquationBalanceCard } from "./EquationBalanceCard";
import { GapFillCard } from "./GapFillCard";
import { QuickChoiceCard } from "./QuickChoiceCard";
import { ReadingStoryCard } from "./ReadingStoryCard";
import { StudyCard } from "./StudyCard";
import { WordTilesCard } from "./WordTilesCard";
import { GeneratedCard } from "./generated/GeneratedCard";
import { type WhiteboardCardEventHandler, whiteboardWriteDelayStyle } from "./types";

const KIND_LABELS: Record<WhiteboardSpec["kind"], string> = {
  gap_fill: "Fill the gap",
  quick_choice: "Quick choice",
  word_tiles: "Build the sentence",
  reading_story: "Reading",
  composed: "Exercise",
  equation_balance: "Equation",
};

const clampPercent = (value: number | undefined): number => {
  if (typeof value !== "number" || Number.isNaN(value)) return 0;
  return Math.min(100, Math.max(0, value));
};

export const DrawingLayer: React.FC<{ drawings: WhiteboardDrawing[] }> = ({ drawings }) => {
  if (drawings.length === 0) return null;

  const bracketPath = (
    x: number,
    y: number,
    width: number,
    height: number,
    side: WhiteboardDrawing["side"] = "left"
  ): string => {
    const right = x + width;
    const bottom = y + height;
    if (side === "right") return `M ${x} ${y} H ${right} V ${bottom} H ${x}`;
    if (side === "top") return `M ${x} ${bottom} V ${y} H ${right} V ${bottom}`;
    if (side === "bottom") return `M ${x} ${y} V ${bottom} H ${right} V ${y}`;
    return `M ${right} ${y} H ${x} V ${bottom} H ${right}`;
  };

  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-10">
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className="absolute inset-0 h-full w-full"
      >
        <defs>
          <marker
            id="whiteboard-arrowhead"
            markerWidth="8"
            markerHeight="8"
            refX="7"
            refY="4"
            orient="auto"
          >
            <path d="M0,0 L8,4 L0,8 Z" fill="#FD954F" />
          </marker>
        </defs>
        {drawings.map((drawing, index) => {
          const x = clampPercent(drawing.x);
          const y = clampPercent(drawing.y);
          const strokeProps = {
            stroke: "#FD954F",
            strokeWidth: 1.2,
            strokeLinecap: "round" as const,
            strokeLinejoin: "round" as const,
            vectorEffect: "non-scaling-stroke" as const,
          };

          if (drawing.shape === "path") {
            const points = (drawing.points ?? [])
              .map((point) => `${clampPercent(point.x)},${clampPercent(point.y)}`)
              .join(" ");
            if (!points) return null;
            return (
              <polyline
                key={`${drawing.shape}-${index}`}
                points={points}
                fill="none"
                opacity={0.95}
                {...strokeProps}
              />
            );
          }

          if (drawing.shape === "arrow" || drawing.shape === "line") {
            return (
              <line
                key={`${drawing.shape}-${index}`}
                x1={x}
                y1={y}
                x2={clampPercent(drawing.x2)}
                y2={clampPercent(drawing.y2)}
                markerEnd={drawing.shape === "arrow" ? "url(#whiteboard-arrowhead)" : undefined}
                {...strokeProps}
              />
            );
          }

          const width = clampPercent(drawing.width);
          const height = clampPercent(drawing.height);
          if (drawing.shape === "circle") {
            return (
              <ellipse
                key={`${drawing.shape}-${index}`}
                cx={x + width / 2}
                cy={y + height / 2}
                rx={Math.max(1, width / 2)}
                ry={Math.max(1, height / 2)}
                fill="rgba(253,149,79,0.08)"
                {...strokeProps}
              />
            );
          }

          if (drawing.shape === "bracket") {
            return (
              <path
                key={`${drawing.shape}-${index}`}
                d={bracketPath(x, y, Math.max(1, width), Math.max(1, height), drawing.side)}
                fill="none"
                {...strokeProps}
              />
            );
          }

          return (
            <rect
              key={`${drawing.shape}-${index}`}
              x={x}
              y={y}
              width={Math.max(1, width)}
              height={Math.max(1, height)}
              rx={2}
              fill="rgba(253,149,79,0.08)"
              {...strokeProps}
            />
          );
        })}
      </svg>
      {drawings
        .filter((drawing) => drawing.label)
        .map((drawing, index) => (
          <span
            key={`label-${index}-${drawing.label}`}
            className="absolute max-w-[10rem] rounded-md border border-[#FD954F]/40 bg-[#1E202F]/95 px-2 py-1 text-xs font-bold leading-tight text-[#FFD9B8] shadow-lg"
            style={{
              left: `${clampPercent(drawing.x)}%`,
              top: `${clampPercent(drawing.y)}%`,
              transform: "translate(0.25rem, -50%)",
            }}
          >
            {drawing.label}
          </span>
        ))}
    </div>
  );
};

interface TargetConnectionLine {
  key: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  label?: string;
}

function findTargetElement(root: HTMLElement, targetId: string): HTMLElement | null {
  const elements = root.querySelectorAll<HTMLElement>("[data-whiteboard-target-id]");
  for (const element of elements) {
    if (element.dataset.whiteboardTargetId === targetId) return element;
  }
  return null;
}

export const TargetConnectionLayer: React.FC<{
  rootRef: React.RefObject<HTMLDivElement | null>;
  connections: WhiteboardConnection[];
  revision: number;
}> = ({ rootRef, connections, revision }) => {
  const [lines, setLines] = useState<TargetConnectionLine[]>([]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    let animationFrame: number | null = null;

    const measure = () => {
      const rootRect = root.getBoundingClientRect();
      const nextLines = connections.flatMap<TargetConnectionLine>((connection, index) => {
        if (!connection.fromId || !connection.toId) return [];
        const fromEl = findTargetElement(root, connection.fromId);
        const toEl = findTargetElement(root, connection.toId);
        if (!fromEl || !toEl) return [];
        const fromRect = fromEl.getBoundingClientRect();
        const toRect = toEl.getBoundingClientRect();
        return [
          {
            key: `${connection.fromId}->${connection.toId}-${index}`,
            x1: fromRect.left + fromRect.width / 2 - rootRect.left,
            y1: fromRect.top + fromRect.height / 2 - rootRect.top,
            x2: toRect.left + toRect.width / 2 - rootRect.left,
            y2: toRect.top + toRect.height / 2 - rootRect.top,
            label: connection.label,
          },
        ];
      });
      setLines(nextLines);
    };
    const scheduleMeasure = () => {
      if (animationFrame !== null) cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(() => {
        animationFrame = null;
        measure();
      });
    };

    measure();
    const resizeObserver =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(scheduleMeasure) : null;
    const mutationObserver =
      typeof MutationObserver !== "undefined" ? new MutationObserver(scheduleMeasure) : null;
    resizeObserver?.observe(root);
    mutationObserver?.observe(root, {
      attributes: true,
      attributeFilter: ["class", "style", "data-whiteboard-target-id"],
      childList: true,
      subtree: true,
    });
    window.addEventListener("resize", scheduleMeasure);
    return () => {
      if (animationFrame !== null) cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      window.removeEventListener("resize", scheduleMeasure);
    };
  }, [connections, revision, rootRef]);

  if (lines.length === 0) return null;

  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-20">
      <svg className="absolute inset-0 h-full w-full">
        <defs>
          <marker
            id="whiteboard-target-arrowhead"
            markerWidth="8"
            markerHeight="8"
            refX="7"
            refY="4"
            orient="auto"
          >
            <path d="M0,0 L8,4 L0,8 Z" fill="#FD954F" />
          </marker>
        </defs>
        {lines.map((line) => (
          <line
            key={line.key}
            x1={line.x1}
            y1={line.y1}
            x2={line.x2}
            y2={line.y2}
            markerEnd="url(#whiteboard-target-arrowhead)"
            stroke="#FD954F"
            strokeLinecap="round"
            strokeWidth={2}
          />
        ))}
      </svg>
      {lines
        .filter((line) => line.label)
        .map((line) => (
          <span
            key={`label-${line.key}`}
            className="absolute max-w-[10rem] rounded-md border border-[#FD954F]/45 bg-[#1E202F]/95 px-2 py-1 text-xs font-bold leading-tight text-[#FFD9B8] shadow-lg"
            style={{
              left: `${(line.x1 + line.x2) / 2}px`,
              top: `${(line.y1 + line.y2) / 2}px`,
              transform: "translate(-50%, -50%)",
            }}
          >
            {line.label}
          </span>
        ))}
    </div>
  );
};

const ConnectionStrip: React.FC<{ connections: WhiteboardConnection[] }> = ({ connections }) => {
  if (connections.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-[#FD954F]/25 bg-[#FD954F]/10 px-3 py-2 text-xs font-semibold text-[#D1D1D6]">
      <ArrowRight size={14} className="text-[#FD954F]" />
      <span className="uppercase tracking-wider text-[#FD954F]">Tutor connections</span>
      {connections.map((connection) => (
        <span
          key={`${connection.from}->${connection.to}`}
          className="inline-flex max-w-full flex-wrap items-center gap-1 rounded-md border border-[#FD954F]/30 bg-[#FD954F]/10 px-2 py-1 text-[#FFD9B8]"
          title={connection.label}
        >
          <span>{connection.from}</span>
          <ArrowRight size={13} className="shrink-0 text-[#FD954F]" />
          <span>{connection.to}</span>
          {connection.label && (
            <span className="basis-full text-[0.7rem] font-medium text-[#D1D1D6]">
              {connection.label}
            </span>
          )}
          {(connection.fromId || connection.toId) && (
            <span className="basis-full text-[0.65rem] font-medium text-[#8E8E93]">
              {[connection.fromId, connection.toId].filter(Boolean).join(" -> ")}
            </span>
          )}
        </span>
      ))}
    </div>
  );
};

export const SceneElementLayer: React.FC<{
  elements: WhiteboardRenderState["elements"];
  photo: boolean;
}> = ({ elements, photo }) => {
  if (elements.length === 0) return null;

  const toneClass = (tone: (typeof elements)[number]["tone"]): string => {
    if (photo) {
      if (tone === "incorrect") return "border-[#A84432]/30 bg-[#FFF3EF] text-[#742C20]";
      if (tone === "success") return "border-[#2E7357]/30 bg-[#EEF8F2] text-[#225841]";
      if (tone === "attention") return "border-[#B46A22]/30 bg-[#FFF7E8] text-[#704114]";
      return "border-[#1E5A86]/20 bg-white/60 text-[#162033]";
    }
    if (tone === "incorrect") return "border-red-400/35 bg-red-400/10 text-red-100";
    if (tone === "success") return "border-emerald-400/30 bg-emerald-400/10 text-emerald-100";
    if (tone === "attention") return "border-[#FD954F]/35 bg-[#FD954F]/10 text-[#FFD9B8]";
    return "border-white/12 bg-white/[0.04] text-white";
  };

  return (
    <div className="grid gap-2" aria-label="Tutor board explanation">
      {elements.map((element) => (
        <div
          key={element.id}
          className={`rounded-lg border px-3 py-2.5 ${toneClass(element.tone)}`}
          data-whiteboard-target-id={`scene_element.${element.id}`}
        >
          {element.label ? (
            <p className="text-[0.7rem] font-bold uppercase tracking-[0.12em] opacity-70">
              {element.label}
            </p>
          ) : null}
          <p
            className={`${element.label ? "mt-1" : ""} ${
              element.type === "equation"
                ? "font-mono text-base font-bold sm:text-lg"
                : element.type === "statement"
                  ? "text-sm font-semibold leading-6"
                  : "text-sm leading-6"
            }`}
          >
            {element.content}
          </p>
        </div>
      ))}
    </div>
  );
};

interface WhiteboardCardProps {
  spec: WhiteboardSpec;
  renderState?: WhiteboardRenderState;
  onEvent?: WhiteboardCardEventHandler;
  variant?: "dark" | "photo";
  /** Extra header content (e.g. a close button) rendered top-right. */
  headerExtra?: React.ReactNode;
}

export const WhiteboardCard: React.FC<WhiteboardCardProps> = ({
  spec,
  renderState = EMPTY_WHITEBOARD_RENDER_STATE,
  onEvent,
  variant = "dark",
  headerExtra,
}) => {
  const hasAnnotation = Boolean(renderState.annotation);
  const hasMarks = renderState.marks.length > 0;
  const hasElements = renderState.elements.length > 0;
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const isPhotoVariant = variant === "photo";
  const allowsTutorMarkup = spec.kind !== "reading_story";

  if ("generated" in spec && spec.generated) return <GeneratedCard reference={spec.generated} />;
  if ("study" in spec && spec.study) {
    return (
      <StudyCard
        key={spec.id}
        exercise={spec.study}
        tiles={spec.kind === "word_tiles" ? spec.tiles : undefined}
        renderState={renderState}
        onEvent={onEvent}
        headerExtra={headerExtra}
      />
    );
  }

  return (
    <div
      className={
        isPhotoVariant
          ? "whiteboard-card--photo relative w-full text-[#162033]"
          : "w-full overflow-hidden rounded-2xl border border-[#FD954F]/30 bg-gradient-to-br from-[#2A2D42] to-[#23263A] shadow-xl shadow-black/40"
      }
    >
      {/* Header */}
      {isPhotoVariant ? (
        headerExtra ? (
          <div className="absolute right-0 top-0 z-30">{headerExtra}</div>
        ) : null
      ) : (
        <div className="flex items-center justify-between border-b border-white/10 bg-white/[0.03] px-4 py-2.5 sm:px-5">
          <div className="flex items-center gap-2">
            <PenLine size={14} className="text-[#FD954F]" />
            <span className="text-xs font-bold uppercase tracking-wider text-[#FD954F]">Board</span>
            <span className="h-1 w-1 rounded-full bg-white/30" />
            <span className="text-xs font-semibold text-[#D1D1D6]">{KIND_LABELS[spec.kind]}</span>
          </div>
          {headerExtra}
        </div>
      )}

      {/* Body */}
      <div
        ref={bodyRef}
        className={isPhotoVariant ? "relative pr-12 pt-2 sm:pr-14" : "relative p-4 sm:p-5"}
      >
        {!isPhotoVariant && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-[0.08]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.9) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.9) 1px, transparent 1px)",
              backgroundSize: "28px 28px",
            }}
          />
        )}
        {allowsTutorMarkup && <DrawingLayer drawings={renderState.drawings} />}
        {allowsTutorMarkup && (
          <TargetConnectionLayer
            rootRef={bodyRef}
            connections={renderState.connections}
            revision={renderState.revision}
          />
        )}
        <div key={spec.id} className="whiteboard-writing-surface relative space-y-4">
          {allowsTutorMarkup && hasElements && (
            <SceneElementLayer elements={renderState.elements} photo={isPhotoVariant} />
          )}
          {allowsTutorMarkup && hasAnnotation && (
            <div className="flex items-start gap-2 rounded-xl border border-[#FD954F]/35 bg-[#FD954F]/10 px-3 py-2 text-sm font-medium leading-relaxed text-[#FFD9B8]">
              <MessageSquareText size={16} className="mt-0.5 shrink-0 text-[#FD954F]" />
              <span>{renderState.annotation}</span>
            </div>
          )}
          {allowsTutorMarkup && hasMarks && (
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-xs font-semibold text-[#D1D1D6]">
              <LocateFixed size={14} className="text-[#FD954F]" />
              <span className="uppercase tracking-wider text-[#FD954F]">Tutor marks</span>
              {renderState.marks.map((mark) => (
                <span
                  key={`${mark.style}-${mark.target}`}
                  className="rounded-md border border-[#FD954F]/30 bg-[#FD954F]/10 px-2 py-1 text-[#FFD9B8]"
                >
                  {mark.label ? `${mark.target}: ${mark.label}` : mark.target}
                  {mark.targetId && (
                    <span className="ml-1 text-[0.65rem] font-medium text-[#8E8E93]">
                      {mark.targetId}
                    </span>
                  )}
                </span>
              ))}
            </div>
          )}
          {allowsTutorMarkup && <ConnectionStrip connections={renderState.connections} />}
          {spec.kind === "gap_fill" && (
            <div data-whiteboard-write-line style={whiteboardWriteDelayStyle(0)}>
              <GapFillCard spec={spec} renderState={renderState} onEvent={onEvent} />
            </div>
          )}
          {spec.kind === "quick_choice" && (
            <div data-whiteboard-write-line style={whiteboardWriteDelayStyle(0)}>
              <QuickChoiceCard spec={spec} renderState={renderState} onEvent={onEvent} />
            </div>
          )}
          {spec.kind === "word_tiles" && (
            <div data-whiteboard-write-line style={whiteboardWriteDelayStyle(0)}>
              <WordTilesCard spec={spec} renderState={renderState} onEvent={onEvent} />
            </div>
          )}
          {spec.kind === "reading_story" && <ReadingStoryCard spec={spec} onEvent={onEvent} />}
          {spec.kind === "composed" && (
            <ComposedCard spec={spec} renderState={renderState} onEvent={onEvent} />
          )}
          {spec.kind === "equation_balance" && (
            <EquationBalanceCard
              spec={spec}
              onEvent={onEvent}
              variant={variant}
              highlightTargets={renderState.highlightTargets}
            />
          )}
        </div>
      </div>
    </div>
  );
};
